import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { getProducts } from '../../../../api/product/queries/getProducts';
import { Button } from '../../../../components/common/buttons/Button';
import { Icon } from '../../../../components/common/icon/Icon';
import { PriceSuggestModal } from '../../../../components/common/modal/PriceSuggestModal';
import { MarketPriceFilterOptionValueSorterEnum } from '../../../../constants/filter/marketPriceFilter.constant';
import {
  filterSliderConstant,
  priceSliderValueMapping,
  PriceSliderValueMappingType,
} from '../../../../constants/filter/slider/filterSliderConstant';
import {
  WHEEL_CONDITION_OPTIONS,
  WheelConditionLevel,
} from '../../../../constants/openMarketProduct/openMarketProduct';
import { marketPricePageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { useGetMachinePriceStatistics } from '../../../../hooks/api/marketPrice/queries/useGetMachinePriceStatistics.query';
import { useGetMachinePriceStatisticsById } from '../../../../hooks/api/marketPrice/queries/useGetMachinePriceStatisticsById.query';
import { useGetNextOpenMarketProductsQuery } from '../../../../hooks/api/product/queries/useGetNextOpenMarketProductsQuery';
import { useGetBannersQuery } from '../../../../hooks/banners/queries/useGetBanners.query';
import { usePopup } from '../../../../hooks/popup/usePopup';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { useGetUserProfile } from '../../../../hooks/user/useGetUserProfile';
import { MachineType } from '../../../../interfaces/Brand.interfaces';
import { IOpenMarketProductDetail } from '../../../../interfaces/OpenMarketProduct.interfaces';
import { IProduct } from '../../../../interfaces/Product.interfaces';
import { MainCarousel } from '../../../../pages/Home/HomePage/components/MainCarousel';
import { imageTypes } from '../../../../store/userSellTractorSurveySlice';
import { formatPrice } from '../../../../utils/formatter/formatPrice.util';
import { formatPublishHistory } from '../../../../utils/formatter/formatPublishHistory.util';
import { getOpenMarketProductTitle } from '../../../../utils/getter/getOpenMarketProductTitle.util';
import { getRegionByDepth, RegionDepth } from '../../../../utils/getter/getShortAddress.util';
import { requestLogin } from '../../../../utils/modal/requestLogin.util';
import { OpenMarketQuadSwiperGroup } from '../components/OpenMarketQuadSwiperGroup';
import { OptionChip, OptionChipSection, OptionChipText } from '../components/OptionChipSection';
import { ProductImageSection } from '../components/ProductImageSection';
import { ProductQuadSwiperGroup } from '../components/ProductQuadSwiperGroup';
import { ProductSpecSummarySection } from '../components/ProductSpecSummarySection';

interface OpenMarketProductSummaryViewProps {
  product: IOpenMarketProductDetail;
  onPriceSuggestClick?: () => void;
  shouldOpenPriceSuggestModal?: boolean;
  onPriceSuggestModalOpened?: () => void;
}

const EmptyMessage = ({ message }: { message: string }) => {
  return <EmptyMessageWrapper>{message}</EmptyMessageWrapper>;
};

export const OpenMarketProductSummaryView = ({
  product,
  onPriceSuggestClick,
  shouldOpenPriceSuggestModal,
  onPriceSuggestModalOpened,
}: OpenMarketProductSummaryViewProps) => {
  // TODO: 스펙 확정나면 useGetProductsQuery 사용하도록 변경
  const navigate = useNavigate();
  const location = useLocation();
  const Popup = usePopup();
  const { productId } = useParams();
  const { isUserLoggedIn } = useCheckUserLoggedIn();
  const [isShowPriceSuggestModal, setIsShowPriceSuggestModal] = useState(false);
  const { userProfile } = useGetUserProfile();

  const isTractor = product.machine.machineType === 'tractor';
  const {
    id: machineId,
    model: modelName,
    brand: { id: brandId, name: brandName },
  } = product.machine;

  const MAX_PRODUCTS_COUNT = 8;
  const pluvaProductsParam = {
    category: 'PLUVA',
    status: 'ONSALE',
    count: MAX_PRODUCTS_COUNT,
  };

  const { data: openMarketProducts, isLoading: isGetOpenMarketProductsQueryLoading } =
    useGetNextOpenMarketProductsQuery({
      searchParam: {
        id: product.id,
      },
    });

  const { data: pluvaProducts } = useQuery<IProduct[], AxiosError>(
    ['products', `category=${pluvaProductsParam.category}`, `status=${pluvaProductsParam.status}`],
    () =>
      getProducts({
        searchParam: pluvaProductsParam,
      }),
  );

  const { data: mainCarouselImages } = useGetBannersQuery({
    searchParam: {
      category: 'CAROUSEL_MAIN',
      // TODO: 추후 outputLocation에 맞는 내용 넣을 수 있도록 리팩토링 필요함.https://gint-team.atlassian.net/browse/PVM-2280
      outputLocation: 'home',
    },
  });

  const { data: machinePriceStatistic, isLoading: isMachinePriceStatisticLoading } = useGetMachinePriceStatisticsById({
    machineId: machineId as string,
    options: {
      enabled: isTractor,
    },
  });

  const hasSameModelMarketPriceData: boolean = Boolean(machinePriceStatistic);

  const { data: machinePriceList } = useGetMachinePriceStatistics({
    searchParam: {
      brand: brandId,
      minEngineOutputPs: filterSliderConstant.engineOutputInPs.min,
      maxEngineOutputPs: filterSliderConstant.engineOutputInPs.max,
      minMedianPrice: filterSliderConstant.medianPrice.min,
      maxMedianPrice: priceSliderValueMapping[filterSliderConstant.medianPrice.max as PriceSliderValueMappingType],
      sort: MarketPriceFilterOptionValueSorterEnum.default_desc,
      page: 1,
      count: 5,
    },
    options: {
      enabled: !isMachinePriceStatisticLoading && !hasSameModelMarketPriceData,
    },
  });

  const hasSameBrandMarketPriceData: boolean = (machinePriceList && machinePriceList?.pages[0]?.length > 0) ?? false;

  const handlePriceSuggestModalOpen = () => {
    ampli.pvmClickOdpSuggestPriceButton({ brand: brandName, model: modelName, title: productTitle });

    if (!isUserLoggedIn) {
      requestLogin(Popup, navigate, () => {
        ampli.pvmClickLoginButtonInOdpSuggestPriceProcess({
          brand: brandName,
          model: modelName,
        });
      });
      return;
    }

    // TODO: 가격 제안 모달 인풋에 원래 가격 표시 구현
    setIsShowPriceSuggestModal(true);
  };

  const getWheelConditionDescription = (
    wheelPair1Condition?: WheelConditionLevel,
    wheelPair2Condition?: WheelConditionLevel,
    machineType?: MachineType,
  ): string => {
    const isCombine = machineType === 'combine';
    const isTransplanter = machineType === 'transplanter';
    const wheelPair1Label = isCombine ? '좌 궤도바퀴' : isTransplanter ? '앞 바퀴' : '앞 타이어';
    const wheelPair2Label = isCombine ? '우 궤도바퀴' : isTransplanter ? '뒷 바퀴' : '뒷 타이어';

    const wheelPair1Description = wheelPair1Condition
      ? `${wheelPair1Label} ${mapWheelConditionToText(wheelPair1Condition)}`
      : '';
    const wheelPair2Description = wheelPair2Condition
      ? `${wheelPair2Label} ${mapWheelConditionToText(wheelPair2Condition)}`
      : '';

    const descriptions = [wheelPair1Description, wheelPair2Description].filter(Boolean);
    return descriptions.length > 0 ? `${descriptions.join(', ')} 👨🏻‍🔧` : '';
  };

  const mapWheelConditionToText = (condition: WheelConditionLevel): string => {
    switch (condition) {
      case WHEEL_CONDITION_OPTIONS.High:
        return '80% 이상';
      case WHEEL_CONDITION_OPTIONS.Medium:
        return '50% 가량 남음';
      case WHEEL_CONDITION_OPTIONS.Low:
        return '50% 이하 남음';
      default:
        return '';
    }
  };

  const hasReplacedParts = product && product.replacedParts && product.replacedParts.length > 0;
  const hasNeedRepairParts = product && product.needRepairParts && product.needRepairParts.length > 0;
  const hasReplacedOrNeedRepairParts = hasReplacedParts || hasNeedRepairParts;

  const machineType = product?.machine?.machineType;
  const isCombine = machineType === 'combine';
  const isTransplanter = machineType === 'transplanter';
  const wheelConditionDescription = isCombine
    ? getWheelConditionDescription(product?.leftTrackCondition, product?.rightTrackCondition, machineType)
    : isTransplanter
      ? getWheelConditionDescription(product?.frontWheelCondition, product?.rearWheelCondition, machineType)
      : getWheelConditionDescription(product?.frontTireCondition, product?.rearTireCondition, machineType);

  const hasEtcFeatures = wheelConditionDescription || product?.directImportYn || product?.priceNegotiationYn;

  const sortedImages = product?.images?.sort((a, b) => {
    const indexA = imageTypes.indexOf(a.type as (typeof imageTypes)[number]);
    const indexB = imageTypes.indexOf(b.type as (typeof imageTypes)[number]);
    return indexA - indexB;
  });

  const productTitle = getOpenMarketProductTitle({ brandName, machine: product.machine });

  const isMyProduct = product?.user?.id === userProfile?.id;

  useEffect(() => {
    if (shouldOpenPriceSuggestModal) {
      setIsShowPriceSuggestModal(true);
      onPriceSuggestModalOpened?.();
    }
  }, [shouldOpenPriceSuggestModal, onPriceSuggestModalOpened]);

  if (product) {
    return (
      <>
        <ProductImageSection
          images={sortedImages}
          status={product.status}
          pageType={'odp'}
          productTitle={productTitle}
          productCode={product.code}
          disableLoginPrompt
        />

        <ProductInfoContainer>
          <LocationAndDate>
            <LocationAndDateText>{getRegionByDepth(product.location, RegionDepth.DISTRICT)}</LocationAndDateText>
            <LocationAndDateText>{'·'}</LocationAndDateText>
            <LocationAndDateText>
              {formatPublishHistory({
                approvalDate: product.approvalDate,
                updatedAt: product.updatedAt,
                lastBumpAt: !product.isNewProduct ? product.bump?.lastBumpAt : null,
              })}
            </LocationAndDateText>
          </LocationAndDate>
          <Section1>
            <Title>{productTitle}</Title>
            <PriceWrapper isPriceBlurred={false}>
              <Price>{`${formatPrice(product.hopeSellingPrice)}원`}</Price>
            </PriceWrapper>
            <ProductInfoSummaryContainer>
              <ProductInfoSummary>{`조회 ${product.views}`}</ProductInfoSummary>
              <ProductInfoSummary>{'·'}</ProductInfoSummary>
              <ProductInfoSummary>{`문자 ${Number(product.pokeCount || 0).toLocaleString()}`}</ProductInfoSummary>
              <ProductInfoSummary>{'·'}</ProductInfoSummary>
              <ProductInfoSummary>{`전화 ${Number(product.inquiryCount || 0).toLocaleString()}`}</ProductInfoSummary>
            </ProductInfoSummaryContainer>
          </Section1>

          <Section2>
            <ProductSpecSummarySection
              manufacturedYear={product.manufacturedYear}
              operationTime={product.operationTime}
              engineOutputInPs={product.machine.engineOutputInPs}
              machineType={product.machine.machineType}
              rowConfig={product.machine.rowConfig}
              isGrayscale
            />

            {isTractor && (hasSameModelMarketPriceData || hasSameBrandMarketPriceData) && (
              <TractorPricePromotionSection>
                <PromoTitleSection>
                  <Icon name={'CircleWon'} width={24} height={24} fill={'#FF9500'} />
                  <PromoTitle>
                    {hasSameModelMarketPriceData ? '이 트랙터는' : '비슷한 매물,'}
                    <PromoTitleHighlight>{' 얼마에 거래'}</PromoTitleHighlight>
                    {'됐을까?'}
                  </PromoTitle>
                </PromoTitleSection>
                <PromoDescription>{'플루바 트랙터 시세에서 살펴보세요'}</PromoDescription>
                <ButtonWrapper>
                  <Button
                    borderType={'round'}
                    color={'filledGreyLight'}
                    size={'sm'}
                    text={{
                      size: 'md',
                      content: `${hasSameModelMarketPriceData ? modelName : brandName} 시세 확인하기`,
                    }}
                    icon={{ fileName: 'AngleRight', size: 'Small', position: 'right', fill: '#505363' }}
                    onClick={() => {
                      ampli.pvmClickViewBrandMarketPrice({
                        title: productTitle,
                      });
                      navigate(
                        `${marketPricePageInfo.searchResultPage.path}?keyword=${brandName} ${
                          hasSameModelMarketPriceData ? modelName : ''
                        }`,
                      );
                    }}
                  />
                </ButtonWrapper>
              </TractorPricePromotionSection>
            )}
          </Section2>

          <HorizontalLine />

          <Section3>
            <SectionTitle>{'매물 옵션'}</SectionTitle>
            {product.optionDetails && product.optionDetails.length > 0 ? (
              <OptionChipSection>
                {product.optionDetails.map((option, index) => (
                  <OptionChip key={option}>
                    <OptionChipText>{option}</OptionChipText>
                  </OptionChip>
                ))}
              </OptionChipSection>
            ) : (
              <EmptyMessage message={'없음'} />
            )}
          </Section3>

          <HorizontalLine />

          {(isTractor || isTransplanter) && (
            <>
              <Section4>
                <SectionTitle>{'작업기 정보'}</SectionTitle>
                {product.workingMachineDetails && product.workingMachineDetails.length > 0 ? (
                  <ProductFeaturesContaier>
                    {product.workingMachineDetails.map((workingMachine) => (
                      <ProductFeatureWrapper key={workingMachine}>
                        <ProductFeatureText>{workingMachine}</ProductFeatureText>
                      </ProductFeatureWrapper>
                    ))}
                  </ProductFeaturesContaier>
                ) : (
                  <EmptyMessage message={'없음'} />
                )}
              </Section4>
              <HorizontalLine />
            </>
          )}

          <Section5>
            <SectionTitle>{'수리가 필요하거나 교체한 부품'}</SectionTitle>
            {hasReplacedOrNeedRepairParts ? (
              <>
                {hasNeedRepairParts && (
                  <ProductPartsContaier>
                    <ProductPartsTitleSection>
                      <Icon name={'ExclamationMarkRed'} width={20} height={20} />
                      <ProductPartsTitle>{'수리가 필요한 항목'}</ProductPartsTitle>
                    </ProductPartsTitleSection>
                    <ProductPartsDescription>
                      {product.needRepairParts.map((needRepairPart) => (
                        <ProductPartDescriptionText
                          key={needRepairPart}
                        >{`• ${needRepairPart}`}</ProductPartDescriptionText>
                      ))}
                    </ProductPartsDescription>
                  </ProductPartsContaier>
                )}
                {hasReplacedParts && (
                  <ProductPartsContaier>
                    <ProductPartsTitleSection>
                      <Icon name={'ArrowUturnRightGreen'} width={20} height={20} />
                      <ProductPartsTitle>{'교체한 부품 내역'}</ProductPartsTitle>
                    </ProductPartsTitleSection>
                    <ProductPartsDescription>
                      {product.replacedParts.map((replacedPart) => (
                        <ProductPartDescriptionText
                          key={replacedPart}
                        >{`• ${replacedPart}`}</ProductPartDescriptionText>
                      ))}
                    </ProductPartsDescription>
                  </ProductPartsContaier>
                )}
              </>
            ) : (
              <EmptyMessage message={'없음'} />
            )}
          </Section5>
          <Section6>
            <SectionTitle>{'판매자의 매물 소개'}</SectionTitle>
            {!hasEtcFeatures && !product.information && <EmptyMessage message={'없음'} />}
            {hasEtcFeatures && (
              <EtcDetailsSection>
                {wheelConditionDescription && (
                  <ProductFeaturesContaier>
                    <ProductFeatureWrapper>
                      <ProductFeatureText>{wheelConditionDescription}</ProductFeatureText>
                    </ProductFeatureWrapper>
                  </ProductFeaturesContaier>
                )}
                {product.directImportYn && (
                  <ProductFeaturesContaier>
                    <ProductFeatureWrapper>
                      <ProductFeatureText>{'해외 직수입 매물 🌟'}</ProductFeatureText>
                    </ProductFeatureWrapper>
                  </ProductFeaturesContaier>
                )}
                {product.priceNegotiationYn && (
                  <ProductFeaturesContaier>
                    <ProductFeatureWrapper>
                      <ProductFeatureText>{'가격 협상 가능 💸'}</ProductFeatureText>
                    </ProductFeatureWrapper>
                  </ProductFeaturesContaier>
                )}
              </EtcDetailsSection>
            )}
            {product.information && <ProductInfomationText>{product.information}</ProductInfomationText>}
          </Section6>
        </ProductInfoContainer>

        {mainCarouselImages && mainCarouselImages.length > 0 && (
          <MainCarousel carouselImages={mainCarouselImages} pageType={'pdp'} position={'bottom'} />
        )}

        <Description>
          <DescriptionTitle>{'고객간 거래에 대하여'}</DescriptionTitle>
          <DescriptionContent>
            {
              '플루바 마켓은 통신판매중개자이며, 통신판매의 당사자가 아닙니다. 전자상거래 등에서의 소비자보호에 관한 법률 등 관련 법령 및 플루바 마켓의 약관에 따라 상품, 상품정보, 거래에 관한 책임은 개별 판매자에게 귀속하고, 플루바 마켓은 원칙적으로 회원간 거래에 대하여 책임을 지지 않습니다. 다만, 플루바 마켓이 직접 판매하는 상품에 대한 책임은 플루바 마켓에게 귀속합니다.'
            }
          </DescriptionContent>
        </Description>

        <ProductInfoContainer>
          {openMarketProducts && openMarketProducts.length >= 4 && (
            <Section6>
              <OpenMarketQuadSwiperGroup
                pageType={'odp'}
                title={'다양한 회원 직거래 매물 더보기'}
                openMarketProducts={openMarketProducts}
                goToButton={{ renderedIn: 'odp', itemType: 'open-market' }}
              />
            </Section6>
          )}
          {pluvaProducts && pluvaProducts.length >= 4 && (
            <Section6>
              <ProductQuadSwiperGroup
                title={'보증을 제공하는 인증 트랙터 더보기'}
                products={pluvaProducts}
                goToButton={{ renderedIn: 'odp', itemType: 'pluva' }}
                pageType={'odp'}
              />
            </Section6>
          )}
        </ProductInfoContainer>
        <PriceSuggestModal
          isShow={isShowPriceSuggestModal}
          onClose={() => setIsShowPriceSuggestModal(false)}
          productId={productId || ''}
          productCode={product.code}
          hopeSellingPrice={product.hopeSellingPrice}
          productTitle={productTitle}
          brandName={brandName}
          modelName={modelName}
          phoneNumber={product.user.phoneNumber}
          machineType={product.machine.machineType}
        />
      </>
    );
  }
  return null;
};

const Section = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  padding: 32px var(--radius-large, 16px);
  gap: 24px;
`;

const LocationAndDate = styled.div`
  display: flex;
  padding: 16px;
  align-items: center;
  gap: 4px;
  align-self: stretch;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const LocationAndDateText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;

const Section1 = styled(Section)`
  padding: 24px 16px 32px 16px;

  gap: 8px;
`;

const Section2 = styled(Section)`
  padding: 0px var(--radius-large, 16px) 40px var(--radius-large, 16px);

  gap: 40px;
`;

const Section3 = styled(Section)``;

const Section4 = styled(Section)``;

const Section5 = styled(Section)``;

const Section6 = styled(Section)``;

const ProductInfoSummaryContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--radius-small, 4px);
`;

const ProductInfoSummary = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */
`;

const TitlePriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  gap: var(--radius-large, 16px);
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const PriceWrapper = styled.div<{ isPriceBlurred: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
  height: ${(props) => (props.isPriceBlurred ? '70px' : 'auto')};

  position: relative;
`;

const Price = styled.span`
  color: var(--Light-Scheme-Color-primary-red, #ff3049);

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const SectionTitle = styled.h3`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 15.3px */
`;

const ProductInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ProductFeaturesContaier = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;

  gap: var(--radius-medium, 8px);
`;

const ProductPartsContaier = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-medium, 8px);
  align-self: stretch;
`;

const ProductPartsTitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-small, 4px);
  align-self: stretch;
`;

const ProductPartsTitle = styled.h4`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 18.2px */
`;

const ProductPartsDescription = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const ProductPartDescriptionText = styled.li`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const EmptyMessageWrapper = styled.span`
  color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);
  text-align: center;

  /* System/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 13.6px */
`;

const ProductFeatureWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  padding: var(--radius-large, 16px);

  gap: var(--radius-medium, 8px);

  border-radius: var(--radius-large, 16px) var(--radius-large, 16px) var(--radius-large, 16px) var(--radius-small, 4px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const ProductFeatureText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const EtcDetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-medium, 8px);
  align-self: stretch;
`;

const ProductInfomationText = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const Description = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;

  padding: 24px;
  gap: var(--radius-medium, 8px);

  border-radius: var(--radius-none, 0px);

  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const DescriptionTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Body/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 18px */
`;

const DescriptionContent = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Body/Small/s-regular */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const HorizontalLine = styled.hr`
  width: 100%;

  height: 0px;
  align-self: stretch;
  stroke-width: 1px;
  stroke: #e4e4ed;
`;

const TractorPricePromotionSection = styled.div`
  display: flex;
  padding: 32px 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const PromoTitleSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
`;

const PromoTitle = styled.h3`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const PromoTitleHighlight = styled.span`
  color: var(--Light-Scheme-Color-primary-orange, #ff9500);
`;

const PromoDescription = styled.p`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const ButtonWrapper = styled.div``;
